import { Maximize2, RotateCcw, X } from "lucide-react";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  type TooltipProps,
  XAxis,
  YAxis,
} from "recharts";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useIsMobile } from "@/lib/hooks/use-mobile";
import { useSalonStore } from "@/lib/store/salon";

import { formatLocalizedDate } from "../../lib/format-localized-date";
import type { RedemptionTrendPointShape } from "../../types/discount-analytics-types";

interface Props {
  data: RedemptionTrendPointShape[];
}

// Parse a `yyyy-MM-dd` date string explicitly in the salon timezone. The
// backend buckets these strings by salon-local day, so we must parse them
// in the same zone — otherwise `DateTime.fromISO("2026-04-01")` falls back
// to `Settings.defaultZone` (or the browser's system zone if that hasn't
// been initialised), which shifts the displayed day for any user east or
// west of the salon's actual timezone.
function parseChartDate(dateStr: string, timezone: string): DateTime {
  return DateTime.fromISO(dateStr, { zone: timezone });
}

function formatPrice(amount: number): string {
  // appliedAmount is in smallest currency unit (cents). Display in major.
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 0,
  }).format(amount / 100);
}

function renderChartTooltip(
  language: string,
  timezone: string,
  t: (key: string) => string
): (props: TooltipProps<number, string>) => React.ReactNode {
  return ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    const dt = parseChartDate(String(label), timezone);
    const dateLabel = dt.isValid ? formatLocalizedDate(dt, "MMM d, yyyy", language) : String(label);
    const point = payload[0]?.payload as RedemptionTrendPointShape | undefined;
    if (!point) return null;

    return (
      <div className="rounded-lg border-2 border-slate-300 bg-white px-3 py-2 shadow-lg">
        <div className="text-sm font-bold text-slate-900">{dateLabel}</div>
        <div className="mt-1 flex items-center justify-between gap-4 text-xs">
          <span className="font-semibold text-slate-700">
            {t("discounts.detail.redemptionCount")}
          </span>
          <span className="font-bold tabular-nums text-rose-700">{point.redemptionCount}</span>
        </div>
        <div className="flex items-center justify-between gap-4 text-xs">
          <span className="font-semibold text-slate-700">
            {t("discounts.detail.amountDiscounted")}
          </span>
          <span className="font-bold tabular-nums text-emerald-700">
            {formatPrice(point.amountDiscounted)}
          </span>
        </div>
      </div>
    );
  };
}

function ChartBody({
  data,
  language,
  timezone,
  t,
}: {
  data: RedemptionTrendPointShape[];
  language: string;
  timezone: string;
  t: (key: string) => string;
}) {
  const formatXAxis = (dateStr: string) => {
    const dt = parseChartDate(dateStr, timezone);
    return dt.isValid ? formatLocalizedDate(dt, "MMM d", language) : dateStr;
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis
          dataKey="date"
          tickFormatter={formatXAxis}
          tick={{ fontSize: 12, fill: "#334155" }}
          tickLine={{ stroke: "#cbd5e1" }}
          axisLine={{ stroke: "#cbd5e1" }}
        />
        <YAxis
          allowDecimals={false}
          tick={{ fontSize: 12, fill: "#334155" }}
          tickLine={{ stroke: "#cbd5e1" }}
          axisLine={{ stroke: "#cbd5e1" }}
        />
        <Tooltip
          cursor={{ stroke: "#f43f5e", strokeWidth: 1, strokeDasharray: "4 4" }}
          content={renderChartTooltip(language, timezone, t)}
        />
        <Line
          type="monotone"
          dataKey="redemptionCount"
          name={t("discounts.detail.redemptionCount")}
          stroke="#e11d48"
          strokeWidth={2.5}
          dot={{ r: 3, fill: "#e11d48", strokeWidth: 0 }}
          activeDot={{ r: 6, fill: "#e11d48", stroke: "#fff", strokeWidth: 2 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

export function DiscountRedemptionTrendChart({ data }: Props) {
  const { t, i18n } = useTranslation("reports");
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  // Always parse the backend's salon-local day strings in the salon's
  // own timezone instead of relying on the Luxon default zone. Fall back
  // to UTC only when the salon (and therefore timezone) isn't loaded yet
  // — in that case the chart isn't queried for data anyway.
  const timezone = useSalonStore((s) => s.currentSalon?.timezone) ?? "UTC";

  // Lock body scroll while the fullscreen view is open so the chart owns the
  // whole viewport instead of jiggling around with the page.
  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  // Desktop: normal chart, no modal.
  if (!isMobile) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">{t("discounts.detail.trendTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="h-64">
          <ChartBody data={data} language={i18n.language} timezone={timezone} t={t} />
        </CardContent>
      </Card>
    );
  }

  // Mobile: blurred preview + tap-to-view CTA. Tapping opens a fullscreen
  // landscape view rotated 90° so the chart has the long axis available
  // without the user needing to manually turn their device.
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">{t("discounts.detail.trendTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="relative h-64 overflow-hidden rounded-b-xl">
          <div className="pointer-events-none select-none blur-[3px] opacity-60 h-full">
            <ChartBody data={data} language={i18n.language} timezone={timezone} t={t} />
          </div>
          <div className="absolute inset-0 flex items-center justify-center bg-white/40 backdrop-blur-[1px]">
            <Button
              type="button"
              onClick={() => setIsOpen(true)}
              size="lg"
              className="shadow-lg flex items-center gap-2 bg-rose-600 hover:bg-rose-700 text-white font-semibold"
            >
              <Maximize2 className="h-4 w-4" />
              {t("discounts.detail.tapToView")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {isOpen ? (
        <FullscreenLandscapeChart
          data={data}
          language={i18n.language}
          timezone={timezone}
          t={t}
          onClose={() => setIsOpen(false)}
        />
      ) : null}
    </>
  );
}

function FullscreenLandscapeChart({
  data,
  language,
  timezone,
  t,
  onClose,
}: {
  data: RedemptionTrendPointShape[];
  language: string;
  timezone: string;
  t: (key: string) => string;
  onClose: () => void;
}) {
  // Anchor a 100vh × 100vw container at the viewport center, then rotate it
  // 90°. After rotation it covers the full viewport edge-to-edge. We DON'T
  // rely on flex-center because the pre-rotate width (= viewport height)
  // exceeds the viewport width and overflows / clips.
  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/95 backdrop-blur-sm">
      {/* Close button stays in screen-coordinate top-right, not rotated */}
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 z-10 rounded-full bg-white/95 p-2 shadow-lg hover:bg-white"
        aria-label={t("discounts.detail.closeChart")}
      >
        <X className="h-5 w-5 text-slate-900" />
      </button>

      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          width: "100vh",
          height: "100vw",
          transform: "translate(-50%, -50%) rotate(90deg)",
          transformOrigin: "center",
        }}
      >
        <div className="h-full w-full bg-white px-6 py-4 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-bold text-slate-900">
              {t("discounts.detail.trendTitle")}
            </h2>
            <span className="text-xs font-medium text-slate-500 flex items-center gap-1">
              <RotateCcw className="h-3 w-3" />
              {t("discounts.detail.landscapeHint")}
            </span>
          </div>
          <div className="flex-1 min-h-0">
            <ChartBody data={data} language={language} timezone={timezone} t={t} />
          </div>
        </div>
      </div>
    </div>
  );
}
